import { Calendar, Clock, Users, Globe, CheckCircle, AlertCircle } from 'lucide-react';

export const TimeSlotItem = ({
  slot,
  index,
  isSelected,
  isBooked,
  isBlocked,
  isPast,
  isSelectable,
  isHovered,
  isDragging,
  handleMouseDown,
  handleMouseEnter,
  handleMouseUp
}) => {
  // Check if this is a conflict slot (from availabilityInfo)
  const isConflict = slot.conflict;
  
  let bgClass     = 'bg-white';
  let cursorClass = 'cursor-pointer';
  let textClass   = 'text-slate-600';

  if (isSelected) {
    bgClass   = 'bg-blue-500';
    textClass = 'text-white';
  } else if (isConflict) {
    bgClass     = 'bg-amber-50';
    cursorClass = 'cursor-not-allowed';
    textClass   = 'text-amber-600';
  } else if (isBlocked) {
    bgClass     = 'bg-slate-100';
    cursorClass = 'cursor-not-allowed';
    textClass   = 'text-slate-500';
  } else if (isBooked) {
    bgClass     = 'bg-red-50';
    cursorClass = 'cursor-not-allowed';
    textClass   = 'text-red-500';
  } else if (isPast) {
    bgClass     = 'bg-gray-100';
    cursorClass = 'cursor-not-allowed';
    textClass   = 'text-gray-400';
  } else if (isHovered && isSelectable && !isDragging) {
    bgClass = 'bg-blue-50';
  }

  if (!isSelectable && !isSelected) {
    cursorClass = 'cursor-not-allowed';
  }

  const title = isConflict
    ? `Time conflict - ${slot.displayLabel}`
    : isBlocked
      ? `It's not available - ${slot.displayLabel}`
    : isBooked
      ? `${slot.status} - ${slot.displayLabel}`
      : isPast
        ? `Past time slot - ${slot.displayLabel}`
        : slot.displayLabel;

  return (
    <div
      data-slot-index={index}
      className={`
        relative h-28 ${cursorClass} transition-all duration-150
        flex flex-col items-center justify-center
        border-r border-slate-200 last:border-r-0
        ${bgClass}
      `}
      onMouseDown={() => handleMouseDown(index)}
      onMouseEnter={() => handleMouseEnter(index)}
      onMouseUp={handleMouseUp}
      title={title}
    >
      {/* ── Only change: text-sm → text-xs + whitespace-nowrap ── */}
      <div className={`text-xs font-semibold whitespace-nowrap ${textClass}`}>
        {slot.rangeLabel}
      </div>

      {isBlocked && (
        <div className="mt-2 rounded-full border border-slate-300 bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Not Available
        </div>
      )}

      {/* Conflict indicator */}
      {isConflict && (
        <div className="absolute top-1 right-1">
          <AlertCircle className="w-4 h-4 text-amber-500" />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TimeSlotLegend — responsive wrap on small screens with optional conflict
// ---------------------------------------------------------------------------
export const TimeSlotLegend = ({ showConflict = false }) => (
  <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 mt-5 text-xs">
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white border-2 border-slate-300 rounded shrink-0" />
      <span className="text-slate-700 font-semibold">Available</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded shrink-0" />
      <span className="text-slate-700 font-semibold">Selected</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 border-2 border-gray-300 rounded shrink-0" />
      <span className="text-slate-700 font-semibold">Past Time</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-slate-200 border-2 border-slate-400 rounded shrink-0" />
      <span className="text-slate-700 font-semibold whitespace-nowrap">Not Available</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-red-50 border-2 border-red-300 rounded shrink-0" />
      <span className="text-slate-700 font-semibold whitespace-nowrap">Reserved/Booked</span>
    </div>
    {showConflict && (
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-amber-50 border-2 border-amber-300 rounded shrink-0" />
        <span className="text-slate-700 font-semibold">Conflict</span>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// SelectedTimeSummary — stacks vertically on mobile, row on desktop
// with custom button text and disabled state
// ---------------------------------------------------------------------------
export const SelectedTimeSummary = ({
  formatSelectedTimeRange,
  getDurationLabel,
  product,
  calculateTotalPrice,
  showMinimumInfo,
  handleSelectProduct,
  customButtonText = 'Select This Space',
  disabled = false
}) => (
  <div className="mt-6 p-4 sm:p-5 rounded-lg shadow-lg bg-gradient-to-r from-blue-500 to-blue-600">

    {/* ── Mobile layout: stacked ── */}
    <div className="flex flex-col gap-4 sm:hidden">
      <div className="grid grid-cols-2 gap-3">
        {/* Time slot */}
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-white/80 font-medium">Time Slot</div>
            <div className="text-sm font-bold text-white truncate">
              {formatSelectedTimeRange()}
            </div>
          </div>
        </div>

        {/* Duration */}
        <div className="min-w-0">
          <div className="text-xs text-white/80 font-medium">Duration</div>
          <div className="text-sm font-bold text-white">{getDurationLabel()}</div>
        </div>

        {/* Capacity (optional) */}
        {product.capacity && (
          <div className="min-w-0">
            <div className="text-xs text-white/80 font-medium">Capacity</div>
            <div className="text-sm font-bold text-white flex items-center gap-1">
              <Users className="w-3.5 h-3.5 shrink-0" />
              {product.capacity} people
            </div>
          </div>
        )}

        {/* Price */}
        <div className="min-w-0">
          <div className="text-xs text-white/80 font-medium">Total</div>
          <div className="text-xl font-black text-white">${calculateTotalPrice()}</div>
        </div>
      </div>

      <button
        onClick={handleSelectProduct}
        disabled={showMinimumInfo || disabled}
        className={`w-full py-3 rounded-lg text-sm font-bold shadow-lg transition-all flex items-center justify-center gap-2 ${
          showMinimumInfo || disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-white text-blue-600 hover:text-blue-800 active:scale-95'
        }`}
      >
        <CheckCircle className="w-5 h-5" />
        {customButtonText}
      </button>
    </div>

    {/* ── Desktop layout: original row ── */}
    <div className="hidden sm:flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 lg:gap-6 flex-wrap">
        {/* Time slot */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-xs text-white/90 font-medium mb-1">Time Slot</div>
            <div className="text-base font-bold text-white">{formatSelectedTimeRange()}</div>
          </div>
        </div>

        <div className="h-12 w-px bg-white/30 hidden lg:block" />

        {/* Duration */}
        <div>
          <div className="text-xs text-white/90 font-medium mb-1">Duration</div>
          <div className="text-base font-bold text-white">{getDurationLabel()}</div>
        </div>

        <div className="h-12 w-px bg-white/30 hidden lg:block" />

        {/* Capacity */}
        {product.capacity && (
          <>
            <div>
              <div className="text-xs text-white/90 font-medium mb-1">Capacity</div>
              <div className="text-base font-bold text-white flex items-center gap-1">
                <Users className="w-4 h-4" />
                {product.capacity} people
              </div>
            </div>
            <div className="h-12 w-px bg-white/30 hidden lg:block" />
          </>
        )}

        {/* Price */}
        <div>
          <div className="text-xs text-white/90 font-medium mb-1">Total Amount</div>
          <div className="text-2xl font-black text-white">${calculateTotalPrice()}</div>
        </div>
      </div>

      <button
        onClick={handleSelectProduct}
        disabled={showMinimumInfo || disabled}
        className={`shrink-0 px-5 lg:px-6 py-3 rounded-lg text-sm font-bold shadow-lg transition-all flex items-center gap-2 ${
          showMinimumInfo || disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-white text-blue-600 hover:text-blue-800'
        }`}
      >
        <CheckCircle className="w-5 h-5" />
        {customButtonText}
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// TimeSlotTips — responsive text and instruction swap for mobile
// ---------------------------------------------------------------------------
export const TimeSlotTips = ({ slotInterval, product }) => (
  <div className="mt-5 text-center rounded-lg p-3 sm:p-4 bg-blue-50 border border-blue-200">
    {/* Mobile tip */}
    <p className="block sm:hidden text-xs sm:text-sm font-medium text-blue-900">
      💡 Tap a slot to select · tap another to extend · tap selected to clear
      {' '}• Each slot = {slotInterval} min • Min booking: 1 hr
      {product.capacity && ` • Capacity: ${product.capacity === 1 ? '1 person' : `${product.capacity} people`}`}
    </p>
    {/* Desktop tip */}
    <p className="hidden sm:block text-sm font-medium text-blue-900">
      💡 Click and drag to select consecutive time slots • Each slot = {slotInterval} minutes • Minimum booking: 1 hour
      {product.capacity && ` • Capacity: ${product.capacity === 1 ? '1 person' : `${product.capacity} people`}`}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// DateInfoBox — with support for custom labels
// ---------------------------------------------------------------------------
export const DateInfoBox = ({ searchCriteria, companyState, customLabel }) => (
  <div className="flex items-center gap-2 sm:gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg shadow-sm border border-slate-200">
    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0" />
    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
      <span className="text-sm font-bold text-slate-900">
        {customLabel || searchCriteria.date}
      </span>
      {companyState?.operatingLocation?.zoneId && (
        <>
          <span className="text-slate-300">•</span>
          <span className="text-xs text-slate-600 font-medium flex items-center gap-1">
            <Globe className="w-3 h-3" />
            {companyState?.operatingLocation?.code === 'US'
              ? 'EST'
              : companyState?.operatingLocation?.code === 'IN'
                ? 'IST'
                : companyState.operatingLocation.zoneId}
          </span>
        </>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// ClearSelectionButton — unchanged logic, tighter on mobile
// ---------------------------------------------------------------------------
export const ClearSelectionButton = ({ clearSelection }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      clearSelection();
    }}
    className="text-xs sm:text-sm font-semibold text-slate-700 hover:text-red-600
      flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg
      hover:bg-red-50 transition-colors border border-slate-200 hover:border-red-200
      whitespace-nowrap"
  >
    Clear Selection
  </button>
);
